import { SIGNUP_ROUTE } from "@Helpers/urlHelpers";
import { isUiNextAllowed, type UiNextGate } from "@Utils/uiNext";

/*
 * When the classic UI shows the "Try the new look" invite (plan notes ui-invite): the floating button that switches a
 * classic user to the new UI without `?ui=next`. Router imports this file eagerly, so it imports no `ds/` or
 * `AppShellNext/` code: the button itself (`AppShellNext/invite/UiInvite`) is a lazy chunk, loaded only while
 * `shouldShowUiInvite` is true. Outside the allowed environments it returns false before reading anything.
 */

/** sessionStorage key set by the invite's close button: hidden for the rest of this browser session. */
export const UI_INVITE_DISMISSED_KEY = "cube.ui.invite.dismissed";

/** What Router knows about this render. */
export interface UiInviteContext {
  /** `useUiNextGate()`: only "off" is classic for this user ("pending" may still turn into the new UI). */
  gate: UiNextGate;
  /** A signed-in user with a loaded portal (`userDetails.id && portalData._id`). */
  signedIn: boolean;
  /** Router's `isPortalExpired`: `PortalIllustration` replaces the app, and the new UI would show it too. */
  portalExpired: boolean;
  /**
   * The page body renders: no `?projectId`, or that project has loaded (`!projectId || projectData._id`). Otherwise
   * Router shows a spinner, then `<Forbidden />` once the fetch has failed: no app page to sit on.
   */
  projectReady: boolean;
  pathname: string;
  /**
   * The route Router renders for `pathname` (`findRoute(pathname, routes)`, routeKind.ts), undefined when no route
   * matches (a blank page). Called only when every other rule holds, so production never scans the route table.
   */
  route: () => UiInviteRoute | undefined;
}

/** The one route flag read here (routes.tsx entries, structurally). */
export interface UiInviteRoute {
  showHeader?: boolean;
}

/**
 * Routes that are not a signed-in app page, whatever their route flags say: the sign-in / sign-up / onboarding /
 * password family, the 403 page and the CUBE AI page (all but 403 are `showHeader: false` today; listed so a flag
 * change cannot surface the invite there).
 */
const EXCLUDED_PATHS: ReadonlySet<string> = new Set([
  "/",
  "/newSignIn",
  SIGNUP_ROUTE,
  "/signupflow",
  "/newInvitation",
  "/invitation",
  "/95789",
  "/reset-password",
  "/resetPassword",
  "/thank-you",
  "/welcome",
  "/subscriptionWarning",
  "/403",
  "/ai/chat",
]);

/** Path prefixes of full-screen or public pages: public share links, the viewer, a CUBE AI conversation. */
const EXCLUDED_PREFIXES: readonly string[] = ["/share/", "/designviewer/", "/ai/chat/"];

/** The full-screen workspace explorers (`/workspace/:id/explorer`, `/workspace/:id/mobileExplorer`). */
const WORKSPACE_EXPLORER = /^\/workspace\/[^/]+\/(?:explorer|mobileExplorer)$/;

/** Path segments of print, PDF and status pages (`:module/pdfExport`, a `print` or `maintenance` page). */
const EXCLUDED_SEGMENT = /(?:^|\/)(?:[^/]*pdfExport|print|maintenance|forbidden)(?:\/|$)/i;

/** Pure: the pathname is a signed-in app page the invite may sit on (route flags aside). */
export function isUiInviteRoute(pathname: string): boolean {
  const path = pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (EXCLUDED_PATHS.has(path)) return false;
  if (EXCLUDED_PREFIXES.some((prefix) => path.startsWith(prefix))) return false;
  if (WORKSPACE_EXPLORER.test(path)) return false;
  return !EXCLUDED_SEGMENT.test(path);
}

/** The close button was used in this browser session. Blocked or missing storage = not dismissed. */
export function isUiInviteDismissed(): boolean {
  try {
    return window.sessionStorage.getItem(UI_INVITE_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

/** Hides the invite for this browser session. Blocked storage: the caller's own state still hides it. */
export function dismissUiInvite(): void {
  try {
    window.sessionStorage.setItem(UI_INVITE_DISMISSED_KEY, "1");
  } catch {
    // Storage blocked or full: hidden until the next render that re-reads it (Router's next route change).
  }
}

/**
 * Pure apart from the session flag: the invite shows in an allowed environment (never production), for a classic
 * user (gate "off") who is signed in, on a matched signed-in app route with a header whose page body renders (not
 * the project spinner or Forbidden), while the portal is live and the invite was not dismissed in this session.
 * Cheapest checks first; the route table is scanned last but one, storage is read last.
 */
export function shouldShowUiInvite(context: UiInviteContext): boolean {
  if (!isUiNextAllowed()) return false;
  if (context.gate !== "off" || !context.signedIn || context.portalExpired || !context.projectReady) return false;
  if (!isUiInviteRoute(context.pathname)) return false;
  const route = context.route();
  if (route === undefined || route.showHeader === false) return false;
  return !isUiInviteDismissed();
}
